"use client";

/**
 * CandidateOperationDisplay - CRUD operation results for candidates
 */

import { useState } from "react";
import Link from "next/link";
import { Tag, Alert } from "@/ui";
import { colors, spacing, textStyles } from "@/ui/tokens";
import { actionLinkClass } from "../utils";
import { Person, Visibility, Edit } from "@mui/icons-material";
import {
  ToolCard,
  ToolHeader,
  ToolBody,
  ToolProgress,
  ToolError,
  cleanErrorMessage,
} from "../../Tool";
import { deriveToolState } from "@/lib/sola/types/tools";
import type { ToolDisplayProps } from "@/lib/sola/types/tools";

interface OperationOutput {
  success?: boolean;
  status?: "success" | "completed" | "error";
  message?: string;
  candidate?: {
    id: string | number;
    uuid?: string;
    name?: string;
  };
  viewUrl?: string;
  editUrl?: string;
  changes?: Array<{
    field: string;
    oldValue?: unknown;
    newValue?: unknown;
  }>;
  error?: string;
}

export function CandidateOperationDisplay({ toolCall, isStreaming }: ToolDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const toolState = deriveToolState(toolCall);
  const { status, isLoading, isError, error } = toolState;

  const result = toolCall.finalOutput as OperationOutput | undefined;
  const isSuccess = result?.success === true || result?.status === "success" || result?.status === "completed";

  return (
    <ToolCard status={status} noPadding>
      <ToolHeader
        title=""
        toolName={toolCall.toolName}
        status={status}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded((prev) => !prev)}
        icon={<Person style={{ fontSize: 18 }} />}
      />

      <ToolBody isOpen={isExpanded}>
        {isLoading && (
          <div style={{ padding: `${spacing[2]}px 0`, textAlign: "center" }}>
            <ToolProgress variant="dots" label="Processing..." />
          </div>
        )}

        {isError && error && <ToolError error={error} />}

        {!isLoading && result && (
          <Alert
            variant={isSuccess ? "success" : "error"}
            title={isSuccess ? "Success" : "Error"}
          >
            <span style={{ ...textStyles.body2 }}>
              {isSuccess
                ? (result.message || "Operation completed successfully")
                : cleanErrorMessage(result.message || result.error || "Operation failed")}
            </span>

            {result.candidate && (
              <div style={{ marginTop: spacing[2], display: "flex", alignItems: "center", gap: spacing[2], flexWrap: "wrap" }}>
                <Tag size="sm" color="neutral">
                  {result.candidate.name || `ID: ${result.candidate.id}`}
                </Tag>
                {result.viewUrl && (
                  <Link
                    href={result.viewUrl}
                    className={actionLinkClass}
                  >
                    <Visibility style={{ fontSize: 14 }} /> View Candidate
                  </Link>
                )}
                {result.editUrl && (
                  <Link
                    href={result.editUrl}
                    className={actionLinkClass}
                  >
                    <Edit style={{ fontSize: 14 }} /> Edit Candidate
                  </Link>
                )}
              </div>
            )}

            {result.changes && result.changes.length > 0 && (
              <div style={{ marginTop: spacing[3] }}>
                <span style={{ ...textStyles.label, color: colors.text.muted, display: "block", marginBottom: spacing[1] }}>
                  Changes Made
                </span>
                <div style={{ display: "flex", flexDirection: "column", gap: spacing[1] }}>
                  {result.changes.map((change, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: spacing[2],
                      }}
                    >
                      <span style={{ ...textStyles.body2, fontWeight: 500 }}>
                        {change.field}
                      </span>
                      <span style={{ ...textStyles.caption, color: colors.text.muted }}>
                        {change.oldValue !== undefined
                          ? `${String(change.oldValue)} → ${String(change.newValue)}`
                          : String(change.newValue)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Alert>
        )}
      </ToolBody>
    </ToolCard>
  );
}

export default CandidateOperationDisplay;
